/**
 * Compute position for a dropdown anchored to a trigger element (position: fixed).
 * Shared by appointment-details-modal and appointments-table.
 */
export interface DropdownPosition {
  top: number;
  left: number;
}

export interface ComputeDropdownPositionOptions {
  dropdownWidth?: number;
  offsetLeft?: number;
  margin?: number;
}

const DEFAULT_DROPDOWN_WIDTH = 155;
const DEFAULT_OFFSET_LEFT = -110;
const DEFAULT_MARGIN = 8;

/**
 * Returns { top, left } for a dropdown below the trigger, clamped to viewport.
 * Run inside requestAnimationFrame for accurate layout (e.g. after scroll).
 */
export function computeDropdownPosition(
  triggerElement: HTMLElement,
  options: ComputeDropdownPositionOptions = {}
): DropdownPosition {
  const rect = triggerElement.getBoundingClientRect();
  const dropdownWidth = options.dropdownWidth ?? DEFAULT_DROPDOWN_WIDTH;
  const offsetLeft = options.offsetLeft ?? DEFAULT_OFFSET_LEFT;
  const margin = options.margin ?? DEFAULT_MARGIN;

  let left = rect.left + offsetLeft;
  if (left < margin) {
    left = margin;
  }
  if (left + dropdownWidth > window.innerWidth - margin) {
    left = window.innerWidth - dropdownWidth - margin;
  }

  return {
    top: rect.bottom + 5,
    left
  };
}

/**
 * Run positioning inside requestAnimationFrame and pass result to a callback.
 * Use when opening a dropdown so layout is accurate (e.g. on iPad).
 */
export function scheduleDropdownPosition(
  triggerElement: HTMLElement,
  options: ComputeDropdownPositionOptions,
  onPosition: (position: DropdownPosition) => void
): void {
  requestAnimationFrame(() => {
    const updatedRect = triggerElement.getBoundingClientRect();
    const position = computeDropdownPosition(
      { getBoundingClientRect: () => updatedRect } as HTMLElement,
      options
    );
    onPosition(position);
  });
}
